import type {
  AssignmentSubmission,
  GetAssignmentSubmissionsQuery,
  PutSubmissionCorrectionBody,
  UpdateSubmissionCommentBody,
  UpdateSubmissionEvaluationBody,
} from '@didaskill/domain/assignment-submissions/types';
import {
  PATCH_ASSIGNMENT_SUBMISSIONS_ACTIONS,
  PATCH_ASSIGNMENT_SUBMISSIONS_DEPOSITS_ACTIONS,
} from '@didaskill/domain/assignments/constants';
import type { SuperAgentError } from '@didaskill/types/misc';
import { isErrorWithStatus } from '@didaskill/utils/misc';

import { HttpRepository } from '../httpRepository';

const ASSIGNMENT_SUBMISSIONS_API_URI = '/api/v1/school/assignment-submissions';
const ASSIGNMENT_SUBMISSIONS_FILES_API_URI = '/api/v1/file/downloads/assignment-submissions';

const e = encodeURIComponent;

export class AssignmentSubmissionsRepository extends HttpRepository {
  /**
   * Get submissions assigned to a corrector.
   *
   * @param query - Query params.
   * @param query.q - Query text.
   * @param query.limit - Limit number of results.
   * @param query.offset - Number of results to skip.
   * @param query.view - View of submissions to return.
   * @returns Submissions.
   */
  async getSubmissions(query?: GetAssignmentSubmissionsQuery): Promise<{
    total: number;
    items: AssignmentSubmission[];
  }> {
    const { body } = await this.http.get(ASSIGNMENT_SUBMISSIONS_API_URI).query(query);

    return body;
  }

  /**
   * Get a submission by its id.
   *
   * @param submissionId - Submission id.
   * @param query - Query.
   * @param query.view - View to return.
   * @returns Submission.
   */
  async getSubmission(submissionId: string): Promise<AssignmentSubmission> {
    const { body } = await this.http.get(`${ASSIGNMENT_SUBMISSIONS_API_URI}/${submissionId}`);

    return body;
  }

  /**
   * Get an assignment submission attachment.
   *
   * @param submissionId - Assignment submission id.
   * @param uploadId - Attachment upload id.
   * @returns File.
   */
  async getAssignmentSubmissionAttachment(
    submissionId: string,
    uploadId: string,
    depositId: string,
  ): Promise<{ type: string; body: Blob }> {
    const { body, type } = await this.http
      .get(
        `${ASSIGNMENT_SUBMISSIONS_FILES_API_URI}/${e(
          submissionId,
        )}/deposits/${e(depositId)}/attachments/${e(uploadId)}`,
      )
      .responseType('blob');

    return { body, type };
  }

  /**
   * Get an assignment submission correction.
   *
   * @param submissionId - Assignment submission id.
   * @param uploadId - Correction upload id.
   * @returns File.
   */
  async getAssignmentSubmissionCorrection(
    submissionId: string,
    uploadId: string,
    depositId: string,
  ): Promise<{ type: string; body: Blob }> {
    const { body, type } = await this.http
      .get(
        `${ASSIGNMENT_SUBMISSIONS_FILES_API_URI}/${e(
          submissionId,
        )}/deposits/${e(depositId)}/correction/attachments/${e(uploadId)}`,
      )
      .responseType('blob');

    return { body, type };
  }

  /**
   * Lock a submission for current user.
   *
   * @param submissionId - Id of the submission to lock.
   * @returns The updated submission.
   */
  async lockSubmission(submissionId: string): Promise<AssignmentSubmission> {
    const { body } = await this.http
      .patch(`${ASSIGNMENT_SUBMISSIONS_API_URI}/${e(submissionId)}`)
      .send({
        action: PATCH_ASSIGNMENT_SUBMISSIONS_ACTIONS.LOCK,
      });

    return body;
  }

  /**
   * Mark a submission as corrected and correct all its deposits at once.
   *
   * @param submissionId - Submission id.
   * @param updates - Correction details.
   * @returns The updated submission.
   */
  async markSubmission(submissionId: string, updates: PutSubmissionCorrectionBody) {
    const { body } = await this.http
      .put(`${ASSIGNMENT_SUBMISSIONS_API_URI}/${e(submissionId)}/correction`)
      .send(updates);

    return body;
  }

  /**
   * Send the correction of a submission.
   *
   * @param submissionId - Submission id.
   * @param params - Parameters of the correction.
   * @param params.grade - Grade of the submission.
   * @param params.comment - Comment of the submission.
   * @param params.corrections - Array of upload id.
   * @returns The updated submission.
   */
  async markDeposit(
    submissionId: string,
    depositId: string,
    params: UpdateSubmissionEvaluationBody,
  ): Promise<AssignmentSubmission> {
    const { body } = await this.http
      .patch(`${ASSIGNMENT_SUBMISSIONS_API_URI}/${e(submissionId)}/deposits/${e(depositId)}`)
      .send({
        action: PATCH_ASSIGNMENT_SUBMISSIONS_DEPOSITS_ACTIONS.EVALUATION,
        ...params,
      });

    return body;
  }

  /**
   * Send a comment on a submission.
   *
   * @param submissionId - Submission id.
   * @param depositId - Deposit id.
   * @param params - Parameters of the comment.
   * @param params.comment - Comment of the submission.
   * @param params.reason - Reason of the comment.
   * @param params.attachments - Array of upload id.
   * @returns - The updated submission.
   */
  async commentDeposit(
    submissionId: string,
    depositId: string,
    params: UpdateSubmissionCommentBody,
  ): Promise<AssignmentSubmission> {
    const { body } = await this.http
      .patch(`${ASSIGNMENT_SUBMISSIONS_API_URI}/${e(submissionId)}/deposits/${e(depositId)}`)
      .send({
        action: PATCH_ASSIGNMENT_SUBMISSIONS_DEPOSITS_ACTIONS.COMMENT,
        ...params,
      });

    return body;
  }

  /**
   * Unlock the submission.
   *
   * @param submissionId - Submission id.
   * @returns The updated submission.
   */
  async unlockSubmission(submissionId: string): Promise<AssignmentSubmission> {
    const { body } = await this.http
      .patch(`${ASSIGNMENT_SUBMISSIONS_API_URI}/${e(submissionId)}`)
      .send({
        action: PATCH_ASSIGNMENT_SUBMISSIONS_ACTIONS.UNLOCK,
      });

    return body;
  }

  /**
   * Allow a student to redo a corrected assignment (SUPER_ADMIN or ANIMATION only).
   *
   * @param submissionId - Submission id.
   * @returns The updated submission.
   */
  async allowAssignmentRedo(submissionId: string): Promise<AssignmentSubmission> {
    const { body } = await this.http
      .patch(`${ASSIGNMENT_SUBMISSIONS_API_URI}/${e(submissionId)}`)
      .send({
        action: PATCH_ASSIGNMENT_SUBMISSIONS_ACTIONS.ALLOW_REDO,
      });

    return body;
  }

  /**
   * Check if a submission archive exists for a specific user, course, and class.
   * This method performs a HEAD request to verify the archive's availability without downloading it.
   *
   * @param userId - The unique identifier of the user whose submissions are archived.
   * @param courseId - The unique identifier of the course.
   * @param classId - The unique identifier of the class.
   * @returns The HTTP status code (200 if archive exists, or the error status code if not found).
   * @throws {Error} Throws an error if the request fails for reasons other than HTTP status codes.
   */
  async checkSubmissionArchive(userId: string, courseId: string, classId: string) {
    try {
      const res = await this.head(
        `${ASSIGNMENT_SUBMISSIONS_FILES_API_URI}/archive/${e(userId)}/${e(courseId)}/${e(classId)}`,
      );

      if (res.status === 200) {
        return { status: 200, lastModified: res.headers['last-modified'] };
      }

      return { status: res.status, lastModified: null };
    } catch (err: unknown) {
      if (isErrorWithStatus(err)) {
        return err.status;
      }
      throw err;
    }
  }

  /**
   * Open and download a submission archive for a specific user, course, and class.
   * This method first checks if the archive exists using a HEAD request, then opens it in the current browser window.
   *
   * @param userId - The unique identifier of the user whose submissions are archived.
   * @param courseId - The unique identifier of the course.
   * @param classId - The unique identifier of the class.
   * @returns Promise that resolves when the archive is opened in the browser.
   * @throws {Error} Throws an error with status code if the archive is not found (status >= 400).
   */
  async openSubmissionsArchive(userId: string, courseId: string, classId: string) {
    const url = `${ASSIGNMENT_SUBMISSIONS_FILES_API_URI}/archive/${e(userId)}/${e(courseId)}/${e(classId)}`;

    // check user archive to handle error in front
    const res = await this.head(url);

    if (res.status >= 400) {
      const err: SuperAgentError = new Error(`HTTP ${res.status}`);
      err.status = res.status;
      throw err;
    }
    // open the archive
    window.open(url, '_self');
  }
}
